import { Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import saveAs from "file-saver";
import { forkJoin, map, of, tap } from "rxjs";

import { Constants } from "../../../../../../constants/constants";
import { AdaaBoolean } from "../../../../../../constants/enums";
import { EntityModelType, UserModelType, WorkflowDiffVersionType } from "../../../../../../models";
import { EntitiesApiService, FilesApiService, UsersApiService } from "../../../../../../services";
import {
  AccordionDiffViewComponent,
  AttachmentDiffViewComponent,
  ListDiffViewComponent,
  NormalDiffViewComponent,
} from "../../_lib";
import {
  AccordionSectionConfigType,
  CompositeSectionConfigType,
  ListSectionConfigType,
  ScreenSectionConfigType,
  ScreenType,
} from "../../wf-difference.types";
import { intpMilestoneScreenConfig, intpProjectScreenConfig } from "./intp-screen-config";

@Component({
  selector: "adaa-intp-screen",
  imports: [
    TranslatePipe,
    NormalDiffViewComponent,
    AttachmentDiffViewComponent,
    AccordionDiffViewComponent,
    ListDiffViewComponent,
  ],
  templateUrl: "./intp-screen.html",
  styleUrl: "./intp-screen.scss",
})
export class IntpScreenComponent implements OnInit {
  private readonly _usersApiService = inject(UsersApiService);
  private readonly _entitiesApiService = inject(EntitiesApiService);
  private readonly _filesApiService = inject(FilesApiService);

  showOnlyNew = input.required<boolean>();
  isNew = input<boolean>(false);
  itemType = input<number>();
  newObject = input<WorkflowDiffVersionType>();
  oldObject = input<WorkflowDiffVersionType>();

  private readonly _newObject = computed<WorkflowDiffVersionType>(() => this.newObject() ?? {});
  private readonly _oldObject = computed<WorkflowDiffVersionType>(() => this.oldObject() ?? {});
  viewOnlyDifferences = input<boolean>(false);

  isScreenReady = signal<boolean>(false);
  entities = signal<EntityModelType[]>([]);
  owners = signal<UserModelType[]>([]);

  newData = computed(() => {
    let object = this._newObject();

    const owners = this.owners();
    if (owners && owners.length > 1) {
      if ("owner" in object) {
        const item = owners.find(({ id }) => id === object.owner);
        if (item) {
          object = {
            ...object,
            ownerNameAE: item.nameAE,
            ownerNameEN: item.nameEN,
          };
        }
      }

      if ("projectCoordinator" in object) {
        const item = owners.find(({ id }) => id === object.projectCoordinator);
        if (item) {
          object = {
            ...object,
            projectCoordinatorNameAE: item.nameAE,
            projectCoordinatorNameEN: item.nameEN,
          };
        }
      }
    }

    return object;
  });
  oldData = computed(() => {
    let object = this._oldObject();

    const owners = this.owners();
    if (owners && owners.length > 1) {
      if ("owner" in object) {
        const item = owners.find(({ id }) => id === object.owner);
        if (item) {
          object = {
            ...object,
            ownerNameAE: item.nameAE,
            ownerNameEN: item.nameEN,
          };
        }
      }

      if ("projectCoordinator" in object) {
        const item = owners.find(({ id }) => id === object.projectCoordinator);
        if (item) {
          object = {
            ...object,
            projectCoordinatorNameAE: item.nameAE,
            projectCoordinatorNameEN: item.nameEN,
          };
        }
      }
    }

    return object;
  });

  config: {
    type: ScreenType;
    sections: (ScreenSectionConfigType | AccordionSectionConfigType)[];
  };
  readonly $asAccordionConfig = ($s: CompositeSectionConfigType) => $s as AccordionSectionConfigType;
  readonly $asListConfig = ($s: CompositeSectionConfigType) => $s as ListSectionConfigType;

  readonly #entityIds = (): number[] => {
    const newEntityId = this._newObject().entityId;
    const oldEntityId = this._oldObject().entityId;
    const ids: number[] = [];

    if (newEntityId) ids.push(newEntityId);
    if (oldEntityId && oldEntityId !== newEntityId) ids.push(oldEntityId);

    return ids;
  };

  readonly #getOwners = () => {
    const entityIds = this.#entityIds();
    if (entityIds.length === 0) return of([]);

    const getOwnersPerEntity$ = entityIds.map((entityId) => this._usersApiService.getOwnersPerEntity(entityId));

    return forkJoin(getOwnersPerEntity$).pipe(
      map((res) => {
        const list = [];
        for (const { responseData } of res) {
          list.push(...(responseData ?? []));
        }
        return list;
      }),
      tap((data) => this.owners.set(data))
    );
  };

  readonly #apiRequests = () => {
    const api$ = [this._entitiesApiService.getAll(AdaaBoolean.Y, AdaaBoolean.Y)];
    const obs$ = forkJoin(api$).pipe(
      tap({
        next: ([entities]) => {
          this.entities.set(entities.responseData as EntityModelType[]);
        },
      })
    );

    return forkJoin([obs$, this.#getOwners()]).subscribe({
      next: () => {
        this.isScreenReady.set(true);
      },
    });
  };

  public ngOnInit() {
    this.config =
      this.itemType() === Constants.CONSTANT_WORKFLOW.INTERNATIONAL_PROJECT
        ? intpProjectScreenConfig
        : intpMilestoneScreenConfig;

    this.#apiRequests();
  }

  public download(data: Record<string, unknown>) {
    data.downloading = true;
    this._filesApiService.download(data.id as number, true).subscribe({
      next: (blob) => {
        saveAs(blob, data.filename as string);
      },
      error: () => {
        data.downloading = false;
      },
      complete: () => {
        data.downloading = false;
      },
    });
  }
}
